if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();

      // FIX: On first page load, no color filter was ever applied — every
      // image showed regardless of the pre-selected variant, and it only
      // started working correctly after the shopper clicked a swatch
      // (which is wired up separately, likely in product-info.js, to call
      // filterMediaByColor on click). This runs the same filter once on
      // initial load using whatever color is already selected in the DOM,
      // so the gallery is correct before any click happens.
      this.applyInitialColorFilter();
    }

    // Attempts to discover the initially-selected color using common
    // Shopify theme patterns, without requiring Liquid changes. Tries,
    // in order:
    //   1. A data-initial-color attribute set directly on this element
    //      (cleanest option if you can add it in Liquid later).
    //   2. A checked radio/select whose name contains "Color" anywhere
    //      on the page (covers Dawn-style variant-selects/pickers).
    //   3. A URL query param named "color" or "Color".
    applyInitialColorFilter() {
      let color = this.dataset.initialColor;

      if (!color) {
        const checkedColorInput = document.querySelector(
          'variant-selects input[type="radio"]:checked[name*="Color" i], ' +
          'variant-selects input[type="radio"]:checked[name*="Colour" i], ' +
          'fieldset input[type="radio"]:checked[name*="Color" i], ' +
          'fieldset input[type="radio"]:checked[name*="Colour" i]'
        );
        if (checkedColorInput) {
          color = checkedColorInput.value || checkedColorInput.dataset.value;
        }
      }

      if (!color) {
        const colorSelect = document.querySelector('select[name*="Color" i], select[name*="Colour" i]');
        if (colorSelect) color = colorSelect.value;
      }

      if (!color) {
        try {
          const params = new URLSearchParams(window.location.search);
          color = params.get('color') || params.get('Color');
        } catch (e) {
          // no-op, URLSearchParams should always exist, but guard anyway
        }
      }

      if (!color) return;

      this.filterMediaByColor(color);

      // Safety net: this runs automatically before any user interaction,
      // using a best-effort guess at the selected color (see comment
      // above). If that guess is wrong or doesn't match this product's
      // data-media-colors values, filterMediaByColor could end up hiding
      // every single image, leaving the customer with a blank gallery —
      // clearly worse than the original bug (showing all images). So：
      // if the filter would hide everything, undo it and fall back to
      // showing all media instead of risking an empty viewer.
      const visibleInViewer = this.elements.viewer.querySelectorAll('[data-media-colors]:not(.media-color-hidden)');
      const anyMediaWithColors = this.elements.viewer.querySelectorAll('[data-media-colors]');
      if (anyMediaWithColors.length > 0 && visibleInViewer.length === 0) {
        this.elements.viewer.querySelectorAll('.media-color-hidden').forEach((el) => el.classList.remove('media-color-hidden'));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('.media-color-hidden').forEach((el) => el.classList.remove('media-color-hidden'));
        }
        if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
      }
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        // Only used for the "stacked" desktop layout, where scrolling the
        // page to the media is the intended behavior.
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    filterMediaByColor(color) {
      if (!color) return;
      const normalized = color.trim().toLowerCase();

      // Generic media (no real color in its alt text -- Size Guide,
      // Features graphic, etc.) should only be visible when the FIRST
      // color option is selected, matching the server-rendered behavior
      // in product-page-layout-1.liquid. Read the first color from a
      // data attribute set by Liquid on <media-gallery> itself, so this
      // JS-driven filtering (which runs on every subsequent swatch
      // click) doesn't diverge from what was already correct on load.
      const firstColor = (this.dataset.firstColor || '').trim().toLowerCase();

      const filterList = (root) => {
        root.querySelectorAll('[data-media-colors]').forEach((el) => {
          const altText = (el.dataset.mediaColors || '').trim();
          const altLower = altText.toLowerCase();
          // FIX: was using altText.includes(normalized), which caused
          // substring collisions — e.g. selecting "Blue" would also match
          // "Dark Blue" because "Dark Blue".includes("blue") is true.
          //
          // Alt text on this store is inconsistent about WHERE the color
          // sits: e.g. "Pewter Blue_Cotton_bedsheets" (color first) vs
          // "Nazaara_Baadal_Blue" (color last). It may also use different
          // separators ("_", ",", or "-"). Spaces INSIDE a color name
          // (e.g. "Pewter Blue", "Dark Blue") are kept intact — only "_",
          // ",", and "-" are treated as real segment breaks. So: split on
          // those into segments, and check whether ANY segment is an
          // EXACT (not partial) match for the selected color. This works
          // regardless of where the color sits, and still prevents
          // "Blue" from matching inside "Dark Blue", since "Dark Blue"
          // stays together as one segment (no "_"/","/"-" splits it).
          const segments = altLower.split(/[_,\-]/).map((s) => s.trim()).filter(Boolean);
          let show;
          if (altText === '') {
            // No real color info in the alt text at all. Show only when
            // the currently selected color is the first color option --
            // if we don't know the first color (no data-first-color was
            // rendered, e.g. product has no Color option), fall back to
            // always showing, same as the original behavior.
            show = firstColor === '' ? true : normalized === firstColor;
          } else {
            show = segments.includes(normalized);
          }
          el.classList.toggle('media-color-hidden', !show);
        });
      };

      filterList(this.elements.viewer);
      if (this.elements.thumbnails) filterList(this.elements.thumbnails);

      if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
        this.elements.viewer.resetPages();
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;
      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      // Intentionally NOT auto-scrolling the thumbnail strip here. Swiping
      // the main image dispatches 'slideChanged' -> onSlideChanged ->
      // setActiveThumbnail on every slide change, and the strip used to
      // scroll itself to keep the matching thumbnail visible. That made
      // the thumbnail row visibly move every time the main image was
      // swiped, which is not wanted: swiping the main image should only
      // affect the main image. The active thumbnail is still marked via
      // aria-current above (and highlighted by CSS), it just no longer
      // auto-scrolls into view. Clicking a thumbnail directly is
      // unaffected, since the user can already see the thumbnail they
      // clicked.
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      // Avoid forcing a reload/reflow if the image is already loaded —
      // resetting .src on a cached image can still trigger a re-decode on
      // some mobile browsers and contributes to layout jumps.
      if (image.complete && image.naturalWidth > 0) {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
        return;
      }
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
